// add-team-pack-submodule-with-retry-on-network-fail.ts
//
// v3: Wrapper retry cho addTeamPackSubmodule (giờ tải tarball, không git submodule).
// Phân loại lỗi từ downloader:
//   - InvalidIdTokenError → token sai/hết hạn/sai domain → KHÔNG retry, prompt avatar login
//   - PackNetworkError → mạng/Supabase down/signed URL hết hạn → retry backoff
//   - PackParseError → tarball hỏng → menu retry/skip/abort
//
// GIỮ tên file + chữ ký export (init-flow-handlers + scaffold-orchestrator import).
import {
  promptRetryOrSkip,
  UserAbortedRecoveryError,
} from "./prompt-recovery-action-on-failure.js";
import { InvalidIdTokenError, PackNetworkError } from "./supabase-pack-downloader.js";
import { addTeamPackSubmodule, TeamPackAccessAbortedError } from "./team-pack-submodule-manager.js";
import { log } from "./terminal-logger.js";

export interface AddTeamPackResult {
  pinnedTag: string | null;
  skipped: boolean;
}

const MAX_NETWORK_RETRIES = 3;

// Wrap addTeamPackSubmodule với retry. Chữ ký giữ nguyên cho caller cũ.
// (ssoEmail + latest từ v2 không còn dùng — giữ param để không vỡ call site, ignore.)
export async function addTeamPackSubmoduleWithRetryOnNetworkFail(
  projectRoot: string,
  version?: string,
  _ssoEmail?: string,
  _latest = false,
): Promise<AddTeamPackResult> {
  let networkAttempt = 0;

  while (true) {
    try {
      const result = await addTeamPackSubmodule(projectRoot, version);
      return { pinnedTag: result.pinnedTag, skipped: false };
    } catch (err) {
      if (err instanceof TeamPackAccessAbortedError) throw err;

      // Token sai/hết hạn → KHÔNG retry (retry vô ích, cần login lại).
      if (err instanceof InvalidIdTokenError) {
        log.error(err.message);
        const action = await promptRetryOrSkip({
          taskName: "Tải team-ai-pack (xác thực NAL)",
          reason: err.message,
          allowSkip: true,
          hint: "Chạy 'avatar login' với tài khoản @nal.vn rồi thử lại. Hoặc skip + 'avatar sync' sau.",
        });
        if (action === "abort") {
          throw new UserAbortedRecoveryError("User abort tại bước xác thực tải pack.");
        }
        if (action === "skip") {
          log.warn(
            "Skip team-ai-pack. Workspace dùng được nhưng chưa có pack. Pull sau qua `avatar sync`.",
          );
          return { pinnedTag: null, skipped: true };
        }
        continue; // retry-same: user đã login lại → thử lại.
      }

      // Mạng → retry tự động backoff vài lần, sau đó hỏi user.
      if (err instanceof PackNetworkError && networkAttempt < MAX_NETWORK_RETRIES) {
        networkAttempt += 1;
        log.warn(
          `Tải pack lỗi mạng (lần ${networkAttempt}/${MAX_NETWORK_RETRIES}): ${err.message}`,
        );
        await sleep(1000 * networkAttempt); // backoff tuyến tính 1s/2s/3s.
        continue;
      }

      // Hết retry mạng HOẶC lỗi khác (parse) → menu retry/skip/abort.
      const message = err instanceof Error ? err.message : String(err);
      const action = await promptRetryOrSkip({
        taskName: "Tải team-ai-pack tarball",
        reason: message,
        allowSkip: true,
        hint: "Network glitch? Retry thường work. Skip → dùng `avatar sync` sau.",
      });
      if (action === "abort") {
        throw new UserAbortedRecoveryError("User abort tại bước tải team-ai-pack.");
      }
      if (action === "skip") {
        log.warn(
          "Skip team-ai-pack. Workspace dùng được nhưng chưa có pack. Pull sau qua `avatar sync`.",
        );
        return { pinnedTag: null, skipped: true };
      }
      networkAttempt = 0; // reset đếm sau khi user chủ động retry.
    }
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
